import { memo } from 'react';
import {
  Card, CardActionArea, CardContent, Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ProfileTypes } from '@sda/enums';
import { withShowFactory } from '@client/decorators/Owner/withShow/withShowFactory';
import { Act } from './Act';
import { Venue } from './Venue';
import { ShowTitle } from './ShowTitle';
import { ReviewSkeleton } from './ReviewSkeleton';
import { ShowDate } from './ShowDate';
import type { PublicReview } from '@sda/interfaces/review';

const useStyles = makeStyles({ name: 'Review' })((theme) => ({
  root: {
    borderRadius: theme.radii.large,
    position: 'relative',
    backgroundColor: theme.palette.info.xlight,
    flex: 'none',

    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  clickable: {
    '&:hover': {
      backgroundColor: theme.palette.warning.xlight,
    },

    '&:hover, &:active, &:focus': {
      boxShadow: theme.shadows[5],
    },
  },
  featured: {
    backgroundColor: theme.palette.grey[200],
  },
}));

type ReviewProps = Pick<PublicReview, 'selectedProfileId' | 'selectedProfileType' | 'description' | 'score'> & {
  readonly reviewId?: string;
  readonly isFeatured: boolean;
  readonly handleClick?: (reviewId: string) => void;
  readonly displayShowTitle?: string;
  readonly disableProfileClick?: boolean;
  readonly className?: string;
};

type ReviewContentProps = Pick<ReviewProps, 'description' | 'selectedProfileId' | 'selectedProfileType' | 'displayShowTitle' | 'score' | 'disableProfileClick'>;

const ReviewContent: React.FC<ReviewContentProps> = ({
  description, selectedProfileId, selectedProfileType, displayShowTitle,
  score, disableProfileClick,
}) => (
  <CardContent>
    {Boolean(displayShowTitle) && (
      <ShowTitle
        showId={displayShowTitle}
        score={score}
      />
    )}

    <Typography
      color="textSecondary"
      gutterBottom
    >
      "
      {description}
      "
    </Typography>

    {selectedProfileId ? (
      <>
        {selectedProfileType === ProfileTypes.ACT ? (
          <Act
            actId={selectedProfileId}
            disableProfileClick={disableProfileClick}
            showId={displayShowTitle}
          />
        ) : (
          <Venue
            venueId={selectedProfileId}
            disableProfileClick={disableProfileClick}
            showId={displayShowTitle}
          />
        )}
      </>
    ) : (
      <Typography
        variant="body2"
        color="textSecondary"
      >
        Side Door Audience Member

        {Boolean(displayShowTitle) && <ShowDate showId={displayShowTitle} />}
      </Typography>
    )}
  </CardContent>
);

const DataComponent = withShowFactory<{ children: React.ReactNode }>({ fallback: ReviewSkeleton, hasLoaderDefault: true })(
  ({ children }) => children,
) as React.FC<{ id: string, children: React.ReactNode }>;

export const Review = memo<ReviewProps>(({
  isFeatured, handleClick, reviewId, className = '',
  displayShowTitle, ...restProps
}) => {
  const { classes, cx } = useStyles();

  if (handleClick && !isFeatured) {
    return (
      <Card className={cx(classes.root, classes.clickable, className)}>
        <CardActionArea
          onClick={() => handleClick(reviewId)}
        >
          {displayShowTitle ? (
            <DataComponent id={displayShowTitle}>
              <ReviewContent
                displayShowTitle={displayShowTitle}
                {...restProps}
              />
            </DataComponent>
          ) : (
            <ReviewContent
              displayShowTitle={displayShowTitle}
              {...restProps}
            />
          )}
        </CardActionArea>
      </Card>
    );
  }

  return (
    <Card className={cx(classes.root, isFeatured && classes.featured, className)}>
      {displayShowTitle ? (
        <DataComponent id={displayShowTitle}>
          <ReviewContent
            displayShowTitle={displayShowTitle}
            {...restProps}
          />
        </DataComponent>
      ) : (
        <ReviewContent
          displayShowTitle={displayShowTitle}
          {...restProps}
        />
      )}
    </Card>
  );
});
